/**
 * Comanda Pro - Dedicated Product Page
 * Comanda Pro POS plans + Menu Digital addon
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor, ChefHat, Bell, BarChart3, Package, Wifi, QrCode,
  Smartphone, ShoppingCart, ArrowRight, Check, X, Star, Clock,
  Users, Zap, Link2, CreditCard, Receipt, Plus, ChevronRight,
  UtensilsCrossed, ClipboardList, ScanLine
} from 'lucide-react';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

// ==================== COMANDA PRO PLANS ====================
const comandaPlans = [
  {
    name: 'Básico',
    subtitle: 'Taquerías y negocios informales',
    price: 299,
    features: [
      { text: 'Punto de venta (POS)', included: true },
      { text: 'Gestión de pedidos', included: true },
      { text: 'Control de inventario', included: true },
      { text: 'Reportes de ventas', included: true },
      { text: 'Sincronización con TodoEnLocal', included: true },
      { text: 'KDS (Pantalla de cocina)', included: false },
      { text: 'Comandero', included: false },
      { text: 'Timbres de facturación', included: false },
      { text: 'Autofactura con QR', included: false },
    ],
    timbres: null,
    highlighted: false
  },
  {
    name: 'Profesional',
    subtitle: 'Restaurantes consolidados',
    price: 599,
    features: [
      { text: 'Punto de venta (POS)', included: true },
      { text: 'Gestión de pedidos', included: true },
      { text: 'Control de inventario', included: true },
      { text: 'Reportes avanzados', included: true },
      { text: 'Sincronización con TodoEnLocal', included: true },
      { text: 'KDS (Pantalla de cocina)', included: true },
      { text: 'Comandero', included: false },
      { text: '50 timbres de facturación/mes', included: true },
      { text: 'Autofactura con QR', included: false },
    ],
    timbres: { included: 50, extraFee: 4.00 },
    highlighted: true
  },
  {
    name: 'Empresarial',
    subtitle: 'Todo incluido, sin límites',
    price: 999,
    features: [
      { text: 'Punto de venta (POS)', included: true },
      { text: 'Gestión de pedidos', included: true },
      { text: 'Control de inventario', included: true },
      { text: 'Reportes avanzados', included: true },
      { text: 'Sincronización con TodoEnLocal', included: true },
      { text: 'KDS (Pantalla de cocina)', included: true },
      { text: 'Comandero', included: true },
      { text: '100 timbres de facturación/mes', included: true },
      { text: 'Autofactura con QR', included: true },
    ],
    timbres: { included: 100, extraFee: 3.00 },
    highlighted: false
  }
];

// ==================== MENU DIGITAL PLANS ====================
const menuDigitalPlans = [
  {
    name: 'Starter',
    price: 299,
    salesIncluded: 75,
    extraSaleFee: 5,
    features: [
      'Menú digital personalizado',
      'Hasta 75 ventas/mes incluidas',
      'Código QR para mesas',
      'Sincronización con Comanda Pro',
      'Notificaciones en tiempo real',
    ],
    highlighted: false
  },
  {
    name: 'Mid',
    price: 449,
    salesIncluded: 150,
    extraSaleFee: 4,
    features: [
      'Todo lo del plan Starter',
      'Hasta 150 ventas/mes incluidas',
      'Personalización de colores y logo',
      'Reportes de ventas detallados',
      'Soporte prioritario',
    ],
    highlighted: true
  },
  {
    name: 'Pro',
    price: 999,
    salesIncluded: 450,
    extraSaleFee: 3,
    features: [
      'Todo lo del plan Mid',
      'Hasta 450 ventas/mes incluidas',
      'Dominio personalizado',
      'Múltiples sucursales',
      'Gestor de cuenta dedicado',
      'Soporte 24/7',
    ],
    highlighted: false
  }
];

const comandaFeatures = [
  {
    icon: Monitor,
    title: 'KDS — Pantalla de Cocina',
    desc: 'Vista en tiempo real de los pedidos entrantes con tiempos, prioridades y estado de preparación.'
  },
  {
    icon: ClipboardList,
    title: 'Comandero',
    desc: 'Toma pedidos desde cualquier dispositivo directo en la mesa del cliente, sin papel.'
  },
  {
    icon: Bell,
    title: 'Notificaciones Instantáneas',
    desc: 'Alertas sonoras y visuales para cada nuevo pedido o cambio de estado.'
  },
  {
    icon: ShoppingCart,
    title: 'Gestión de Pedidos',
    desc: 'Administra pedidos en sala, para llevar y delivery desde un solo lugar.'
  },
  {
    icon: Package,
    title: 'Control de Inventario',
    desc: 'Seguimiento de ingredientes y productos con alertas de stock bajo.'
  },
  {
    icon: Receipt,
    title: 'Facturación Electrónica',
    desc: 'Timbrado de facturas CFDI integrado. Incluye autofactura con QR en plan Empresarial.'
  },
  {
    icon: BarChart3,
    title: 'Reportes de Ventas',
    desc: 'Estadísticas diarias, semanales y mensuales con gráficas interactivas.'
  },
  {
    icon: Link2,
    title: 'Sincroniza con TodoEnLocal',
    desc: 'Publica tus productos automáticamente en el marketplace y recibe pedidos online.'
  }
];

const ComandaProPage = () => {
  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLORS.darkSlate} 0%, #1a1a2e 50%, ${COLORS.darkSlate} 100%)` }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-40 h-40 rounded-full" style={{ background: `radial-gradient(circle, ${COLORS.orange}40, transparent)` }} />
          <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full" style={{ background: `radial-gradient(circle, ${COLORS.orange}20, transparent)` }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 relative">
          <div className="md:flex items-center gap-12">
            <div className="flex-1 text-center md:text-left mb-10 md:mb-0">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur rounded-full text-white/80 text-sm mb-6">
                <Zap className="w-4 h-4" style={{ color: COLORS.orange }} />
                Software para restaurantes
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
                enLocal<br />
                <span style={{ color: COLORS.orange }}>Comanda Pro</span>
              </h1>
              <p className="text-slate-300 text-base md:text-lg mb-8 max-w-lg">
                El sistema de punto de venta y comandas diseñado para restaurantes, cafeterías y negocios de comida.
                Desde taquerías hasta cadenas de restaurantes.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <a
                  href="#planes"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-semibold transition-transform hover:scale-105"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  Ver planes y precios
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#menu-digital"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-semibold bg-white/10 hover:bg-white/20 transition-colors"
                >
                  Menú Digital
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Hero visual */}
            <div className="flex-1 flex justify-center">
              <div className="relative w-64 h-64 md:w-80 md:h-80">
                <div className="absolute inset-0 rounded-3xl rotate-6 opacity-20" style={{ backgroundColor: COLORS.orange }} />
                <div className="absolute inset-0 rounded-3xl bg-white/5 backdrop-blur border border-white/10 flex flex-col items-center justify-center p-6">
                  <Monitor className="w-16 h-16 md:w-20 md:h-20 mb-4" style={{ color: COLORS.orange }} />
                  <div className="w-full space-y-2">
                    {['Pedido #142 — Mesa 5', 'Pedido #143 — Para llevar', 'Pedido #144 — Delivery'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
                        <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-green-400' : i === 1 ? 'bg-yellow-400' : 'bg-blue-400'}`} />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Todo lo que necesitas para tu restaurante</h2>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Comanda Pro integra todas las herramientas esenciales para gestionar tu negocio de comida de manera eficiente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {comandaFeatures.map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-5 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${COLORS.orange}12` }}>
                <f.icon className="w-5 h-5" style={{ color: COLORS.orange }} />
              </div>
              <h3 className="font-bold text-slate-800 text-sm mb-1">{f.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works - workflow */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Flujo de trabajo simplificado</h2>
            <p className="text-slate-500">Del pedido a la entrega en simples pasos</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: 1, icon: Smartphone, title: 'Cliente pide', desc: 'En sala, QR digital, comandero o marketplace' },
              { step: 2, icon: Bell, title: 'Llega el pedido', desc: 'Notificación instantánea en tu POS y KDS' },
              { step: 3, icon: ChefHat, title: 'Cocina prepara', desc: 'Pantalla de cocina con tiempos y prioridades' },
              { step: 4, icon: Check, title: 'Listo y entregado', desc: 'Cierre de pedido, facturación y registro' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="relative mb-4 inline-block">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto" style={{ backgroundColor: `${COLORS.navyBlue}10` }}>
                    <item.icon className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs"
                        style={{ backgroundColor: COLORS.orange }}>
                    {item.step}
                  </span>
                </div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== COMANDA PRO PRICING ==================== */}
      <section id="planes" className="scroll-mt-16 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 rounded-full text-slate-600 text-sm mb-4">
              <CreditCard className="w-4 h-4" style={{ color: COLORS.orange }} />
              Planes y precios
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Elige el plan ideal para tu negocio</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Desde taquerías y negocios informales hasta cadenas de restaurantes. Sin comisiones por venta, solo una mensualidad fija.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {comandaPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl overflow-hidden transition-shadow flex flex-col ${
                  plan.highlighted
                    ? 'shadow-xl border-2 ring-1 ring-orange-100'
                    : 'border border-slate-200 hover:shadow-lg'
                }`}
                style={plan.highlighted ? { borderColor: COLORS.orange } : {}}
              >
                {plan.highlighted && (
                  <div className="text-center py-1.5 text-white text-xs font-bold tracking-wide uppercase" style={{ backgroundColor: COLORS.orange }}>
                    Más popular
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="mb-5">
                    <h3 className="font-bold text-slate-800 text-lg">{plan.name}</h3>
                    <p className="text-xs text-slate-400">{plan.subtitle}</p>
                  </div>

                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-extrabold" style={{ color: COLORS.navyBlue }}>${plan.price.toLocaleString()}</span>
                    <span className="text-slate-500 text-sm">/mes</span>
                  </div>
                  {plan.timbres && (
                    <p className="text-xs text-slate-400 mb-5">
                      {plan.timbres.included} timbres incluidos · ${plan.timbres.extraFee.toFixed(2)} c/u adicional
                    </p>
                  )}
                  {!plan.timbres && <p className="text-xs text-slate-400 mb-5">Sin facturación electrónica</p>}

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-sm ${f.included ? 'text-slate-700' : 'text-slate-300'}`}>
                        {f.included ? (
                          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.highlighted ? COLORS.orange : COLORS.navyBlue }} />
                        ) : (
                          <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-300" />
                        )}
                        {f.text}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contacto"
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
                      plan.highlighted
                        ? 'text-white hover:opacity-90'
                        : 'text-slate-700 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    style={plan.highlighted ? { backgroundColor: COLORS.orange } : {}}
                  >
                    Contratar {plan.name}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Timbres example */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 max-w-md mx-auto">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Ejemplo de facturación — Plan Profesional</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Mensualidad</span>
                <span className="font-semibold text-slate-800">$599.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Timbres incluidos</span>
                <span className="font-semibold text-slate-800">50</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Si timbras 70 facturas en el mes:</span>
                <span />
              </div>
              <div className="flex justify-between pl-4">
                <span className="text-slate-600">20 timbres adicionales × $4.00</span>
                <span className="font-medium text-orange-600">+$80.00</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="font-semibold text-slate-800">Total del mes</span>
                <span className="font-bold text-lg" style={{ color: COLORS.navyBlue }}>$679.00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== COMPARISON TABLE ==================== */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Comparativa de planes</h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl border border-slate-200 overflow-hidden text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-4 font-semibold text-slate-600 bg-slate-50">Funcionalidad</th>
                  <th className="p-4 font-semibold text-slate-800 text-center bg-slate-50">Básico<br/><span className="font-normal text-xs text-slate-400">$299/mes</span></th>
                  <th className="p-4 font-bold text-center" style={{ backgroundColor: `${COLORS.orange}08`, color: COLORS.orange }}>Profesional<br/><span className="font-normal text-xs">$599/mes</span></th>
                  <th className="p-4 font-semibold text-slate-800 text-center bg-slate-50">Empresarial<br/><span className="font-normal text-xs text-slate-400">$999/mes</span></th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Punto de venta (POS)', basic: true, pro: true, enterprise: true },
                  { feature: 'Gestión de pedidos', basic: true, pro: true, enterprise: true },
                  { feature: 'Control de inventario', basic: true, pro: true, enterprise: true },
                  { feature: 'Reportes de ventas', basic: 'Básicos', pro: 'Avanzados', enterprise: 'Avanzados' },
                  { feature: 'Sincroniza con TodoEnLocal', basic: true, pro: true, enterprise: true },
                  { feature: 'KDS (Pantalla de cocina)', basic: false, pro: true, enterprise: true },
                  { feature: 'Comandero', basic: false, pro: false, enterprise: true },
                  { feature: 'Timbres de facturación', basic: '—', pro: '50/mes', enterprise: '100/mes' },
                  { feature: 'Timbre adicional', basic: '—', pro: '$4.00', enterprise: '$3.00' },
                  { feature: 'Autofactura con QR', basic: false, pro: false, enterprise: true },
                  { feature: 'Soporte', basic: 'Email', pro: 'Prioritario', enterprise: '24/7' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 text-slate-700 font-medium">{row.feature}</td>
                    {[row.basic, row.pro, row.enterprise].map((val, j) => (
                      <td key={j} className={`p-4 text-center ${j === 1 ? 'bg-orange-50/30' : ''}`}>
                        {val === true ? (
                          <Check className="w-5 h-5 mx-auto" style={{ color: j === 1 ? COLORS.orange : COLORS.navyBlue }} />
                        ) : val === false ? (
                          <X className="w-5 h-5 mx-auto text-slate-300" />
                        ) : (
                          <span className="text-slate-600 text-xs font-medium">{val}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ==================== MENU DIGITAL ADDON ==================== */}
      <section id="menu-digital" className="scroll-mt-16 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${COLORS.orange}12`, color: COLORS.orange }}>
              <Plus className="w-4 h-4" />
              Addon de Comanda Pro
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Menú Digital</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Genera una página personalizada de tu restaurante donde tus clientes pueden ver tu menú y ordenar directamente.
              Todas las ventas se sincronizan con tu punto de venta Comanda Pro.
            </p>
          </div>

          {/* Menu Digital benefits */}
          <div className="grid md:grid-cols-3 gap-5 mb-14">
            {[
              {
                icon: QrCode,
                title: 'Código QR para Mesas',
                desc: 'Cada mesa tiene su QR. El cliente escanea, ve el menú y ordena desde su celular.'
              },
              {
                icon: Wifi,
                title: 'Sincronización en Tiempo Real',
                desc: 'Cada pedido llega directamente a Comanda Pro. Sin intermediarios, sin retrasos.'
              },
              {
                icon: CreditCard,
                title: 'Sin Comisión por Venta',
                desc: 'No cobramos porcentaje de comisión. Pagas una mensualidad fija con ventas incluidas.'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-100 p-6 text-center hover:shadow-md transition-shadow">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${COLORS.navyBlue}10` }}>
                  <item.icon className="w-7 h-7" style={{ color: COLORS.navyBlue }} />
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* How pricing works explanation */}
          <div className="bg-gradient-to-br from-slate-50 to-orange-50/30 rounded-2xl p-6 md:p-8 border border-slate-100 mb-10">
            <h3 className="font-bold text-slate-800 text-lg mb-4 text-center">¿Cómo funciona el precio del Menú Digital?</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm" style={{ backgroundColor: COLORS.navyBlue }}>1</div>
                <p className="font-semibold text-slate-800 text-sm mb-1">Elige tu paquete</p>
                <p className="text-xs text-slate-500">Cada paquete incluye una cantidad de ventas mensuales.</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm" style={{ backgroundColor: COLORS.navyBlue }}>2</div>
                <p className="font-semibold text-slate-800 text-sm mb-1">Vende sin comisiones</p>
                <p className="text-xs text-slate-500">Las ventas dentro de tu paquete no tienen costo adicional.</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm" style={{ backgroundColor: COLORS.navyBlue }}>3</div>
                <p className="font-semibold text-slate-800 text-sm mb-1">Ventas adicionales</p>
                <p className="text-xs text-slate-500">Si superas tu límite, solo se cobra un fee por cada venta extra.</p>
              </div>
            </div>
          </div>

          {/* Menu Digital Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-5">
            {menuDigitalPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl overflow-hidden transition-shadow flex flex-col ${
                  plan.highlighted
                    ? 'shadow-xl border-2 ring-1 ring-orange-100'
                    : 'border border-slate-200 hover:shadow-lg'
                }`}
                style={plan.highlighted ? { borderColor: COLORS.orange } : {}}
              >
                {plan.highlighted && (
                  <div className="text-center py-1.5 text-white text-xs font-bold tracking-wide uppercase" style={{ backgroundColor: COLORS.orange }}>
                    Más popular
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-bold text-slate-800 text-lg mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-extrabold" style={{ color: COLORS.navyBlue }}>${plan.price.toLocaleString()}</span>
                    <span className="text-slate-500 text-sm">/mes</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-5">
                    {plan.salesIncluded} ventas incluidas · ${plan.extraSaleFee.toFixed(2)} por venta adicional
                  </p>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.highlighted ? COLORS.orange : COLORS.navyBlue }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contacto"
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
                      plan.highlighted
                        ? 'text-white hover:opacity-90'
                        : 'text-slate-700 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    style={plan.highlighted ? { backgroundColor: COLORS.orange } : {}}
                  >
                    Contratar Menú Digital
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Extra fee example */}
          <div className="mt-8 bg-white rounded-xl border border-slate-200 p-5 max-w-md mx-auto">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Ejemplo — Menú Digital Mid</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Mensualidad</span>
                <span className="font-semibold text-slate-800">$449.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Ventas incluidas</span>
                <span className="font-semibold text-slate-800">150 ventas</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Si vendes 180 en el mes:</span>
                <span />
              </div>
              <div className="flex justify-between pl-4">
                <span className="text-slate-600">30 ventas adicionales × $4.00</span>
                <span className="font-medium text-orange-600">+$120.00</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="font-semibold text-slate-800">Total del mes</span>
                <span className="font-bold text-lg" style={{ color: COLORS.navyBlue }}>$569.00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sync with TodoEnLocal callout */}
      <section className="bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 md:flex items-center gap-8">
            <div className="flex-shrink-0 mb-6 md:mb-0 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl" style={{ backgroundColor: `${COLORS.orange}12` }}>
                <Link2 className="w-8 h-8" style={{ color: COLORS.orange }} />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Sincronización con TodoEnLocal</h3>
              <p className="text-sm text-slate-500 mb-4">
                Comanda Pro se conecta con el marketplace TodoEnLocal para que tus productos estén disponibles online automáticamente.
                Los pedidos del marketplace llegan directamente a tu punto de venta, sin configuración extra.
              </p>
              <Link
                to="/precios"
                className="inline-flex items-center gap-2 text-sm font-medium"
                style={{ color: COLORS.orange }}
              >
                Ver todos los precios y productos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20" style={{ background: `linear-gradient(135deg, ${COLORS.darkSlate} 0%, #1a1a2e 100%)` }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">¿Listo para modernizar tu restaurante?</h2>
          <p className="text-slate-300 mb-8 text-sm md:text-base">
            Solicita una demostración gratuita de Comanda Pro y descubre cómo simplificar la operación de tu negocio.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-semibold text-sm transition-transform hover:scale-105"
              style={{ backgroundColor: COLORS.orange }}
            >
              Solicitar demo gratuita
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/precios"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-semibold text-sm bg-white/10 hover:bg-white/20 transition-colors"
            >
              Ver todos los precios
            </Link>
          </div>
        </div>
      </section>
    </MarketplaceLayout>
  );
};

export default ComandaProPage;
